import { createFileRoute, useRouter } from '@tanstack/react-router'
import { requirePermission } from '@/lib/permissions/permissions'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'
import { MoreHorizontal, Slash, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Exercise,
  Workout,
} from '@/components/workouts/workoutsColumns'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import { useOrg } from '@/lib/states/session/hooks'
import {
  apiWorkoutToEditor,
  editorWorkoutToSavePayload,
  ensureWorkoutSections,
} from '@/lib/utils/workoutSections'
import { patchListCache } from '@trainrr/api-client'
import { useNavbar } from '@/components/navbar'
import EditWorkoutView from '@/components/workouts/editWorkoutView'
import LoadingSpinner from '@/components/LoadingSpinner'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { ButtonGroup, ButtonGroupSeparator } from '@/components/ui/button-group'
import DeleteAlert from '@/components/DeleteAlert'
import AlertBlocker from '@/components/AlertBlocker'
import ExerciseSelectList from '@/components/workouts/ExerciseSelectList'
import AiWorkoutBar from '@/components/workouts/ai/AiWorkoutBar'
import WorkoutEditorWorkspace from '@/components/workouts/WorkoutEditorWorkspace'
import { createWorkoutExercise } from '@/components/workouts/workoutEditorUtils'
import { Tables } from '@/lib/supabase/supabaseTypes'
import update from 'immutability-helper'
import React from 'react'
import InputFieldEditButton from '@/components/inputFieldEditButton'
import { useDocumentTitle } from '@/lib/hooks/useDocumentTitle'
import { useEditorHistory } from '@/lib/hooks/useEditorHistory'
import type { EditorCommitOptions } from '@/lib/hooks/useEditorHistory'
import { toWorkoutEditorDocument } from '@/lib/editorHistory/documents'
import { EditorUndoControls } from '@/components/planHistory/EditorUndoControls'
import {
  editorPageRailClassName,
  editorPageWorkspaceClassName,
} from '@/components/editor/pageLayout'
import { cn } from '@/lib/utils/ui'

const WORKOUT_SIDEBAR_STICKY_CLASS =
  'top-[4.75rem] h-[calc(100svh-9rem)]'

type WorkoutDetail = Awaited<ReturnType<typeof trpcUtils.workouts.get.ensureData>>

function toEditorWorkout(data: WorkoutDetail): Workout {
  return apiWorkoutToEditor(data)
}

export const Route = createFileRoute('/_auth/_dashboard/workouts/$workoutId')({
  beforeLoad: () => {
    requirePermission('workouts.write')
  },
  component: WorkoutView,
  loader: ({ params }) =>
    trpcUtils.workouts.get.ensureData({ workoutId: params.workoutId }),
})

function WorkoutView() {
  const { workoutId } = Route.useParams()
  const org = useOrg()
  const { data } = trpc.workouts.get.useQuery({ workoutId })
  if (!data || !org) return <LoadingSpinner />
  return (
    <WorkoutEditor
      key={data.id}
      org={org}
      loadedWorkout={toEditorWorkout(data)}
    />
  )
}

function WorkoutEditor({
  org,
  loadedWorkout,
}: {
  org: NonNullable<ReturnType<typeof useOrg>>
  loadedWorkout: Workout
}) {
  const { setContent } = useNavbar()
  const { workoutId } = Route.useParams()
  const router = useRouter()

  const history = useEditorHistory(
    toWorkoutEditorDocument({
      workout: loadedWorkout,
      name: loadedWorkout.name,
      description: loadedWorkout.description ?? '',
    }),
  )
  const { workout, name, description } = history.present
  const hasChanges = history.hasChanges
  const [loading, setLoading] = useState(false)
  const [planDeleteAlert, setPlanDeleteAlert] = useState(false)

  const saveWorkout = trpc.workouts.save.useMutation()
  const deleteWorkout = trpc.workouts.delete.useMutation()
  const utils = trpc.useUtils()

  useDocumentTitle(name || workout?.name || 'Workout')

  const bottomRef = useRef<HTMLDivElement>(null)

  const updateWorkout = useCallback(
    (nextWorkout: Workout, options?: EditorCommitOptions) => {
      history.commitDoc(
        (prev) => ({ ...prev, workout: nextWorkout }),
        options,
      )
    },
    [history.commitDoc],
  )

  const handleUndo = useCallback(() => Boolean(history.undo()), [history.undo])
  const handleRedo = useCallback(() => Boolean(history.redo()), [history.redo])

  const breadcrumb = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to="/workouts">Workouts</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <Slash />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{`${workout?.name}`}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    [workout?.name],
  )

  useEffect(() => {
    setContent(breadcrumb)
    return () => setContent(null)
  }, [breadcrumb, setContent])

  const onSave = async () => {
    if (!org && !workout) {
      console.error('Org or workout not found, not saving')
      return
    }

    setLoading(true)

    try {
      const data = await saveWorkout.mutateAsync({
        payload: editorWorkoutToSavePayload(workout, {
          orgId: org?.id ?? '',
          name,
          description,
          isTemplate: true,
        }),
      })

      if (data) {
        await Promise.all([
          utils.workouts.get.invalidate({ workoutId }),
          utils.workouts.list.invalidate(),
        ])
      }
    } catch (error) {
      console.error('error adding workout', error)
      errorToast(error instanceof Error ? error : null)
      setLoading(false)
      return
    }

    setLoading(false)
    history.rebaseline()
    router.invalidate()
    toast.success('Workout saved')
  }

  const removeExercise = (exercise: Exercise) => {
    history.commitDoc((prev) => {
      const w = { ...prev.workout }
      const newExerciseCollection = [...w.exerciseCollection]

      if (newExerciseCollection[exercise.ex_order].length === 1) {
        newExerciseCollection.splice(exercise.ex_order, 1)
      } else {
        const currentCollection = [...newExerciseCollection[exercise.ex_order]]
        currentCollection.splice(exercise.sub_order, 1)
        newExerciseCollection[exercise.ex_order] = currentCollection
      }
      newExerciseCollection.forEach((ex, i) => {
        ex.forEach((e, index) => {
          e.ex_order = i
          e.sub_order = index
        })
      })

      return {
        ...prev,
        workout: {
          ...w,
          exerciseCollection: newExerciseCollection,
        },
      }
    })
  }

  const handleExerciseSelectList = (exercise: Tables<'exercises'>) => {
    history.commitDoc((prev) => {
      const newExercise = createWorkoutExercise({
        exercise,
        exOrder: prev.workout.exerciseCollection?.length ?? 0,
        subOrder: 0,
        sectionId: ensureWorkoutSections(prev.workout).sections.at(-1)?.id,
      })
      return {
        ...prev,
        workout: update(prev.workout, {
          exerciseCollection: { $push: [[newExercise]] },
        }),
      }
    })
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 200)
  }

  const handleDeleteWorkout = async () => {
    try {
      await deleteWorkout.mutateAsync({ workoutId })
      patchListCache(utils.workouts.list, (old) =>
        old.filter((row) => row.id !== workoutId),
      )
    } catch (error) {
      console.error('error deleting workout', error)
      errorToast(error instanceof Error ? error : null)
      return
    }

    router.navigate({ to: '/workouts' })
    toast.success('Workout deleted')
  }

  return (
    <>
      <AlertBlocker shouldBlock={hasChanges} />
      <DeleteAlert
        title={`Delete ${name}`}
        description={`Are you sure you want to delete ${name}?`}
        open={planDeleteAlert}
        onCancel={() => setPlanDeleteAlert(false)}
        onDelete={handleDeleteWorkout}
      />
      <div className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur">
        <div
          className={cn(
            editorPageRailClassName,
            'flex flex-col gap-3 py-3 md:flex-row md:items-center md:justify-between',
          )}
        >
          <div className="min-w-0 flex-1">
            <InputFieldEditButton
              value={name}
              setValue={(value) => {
                history.commitDoc(
                  (prev) => ({ ...prev, name: value }),
                  { coalesceKey: 'plan-name' },
                )
              }}
              placeholder="New Workout"
              className="min-w-0 flex-1 text-2xl font-bold"
            />
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end">
            <EditorUndoControls
              canUndo={history.canUndo}
              canRedo={history.canRedo}
              onUndo={handleUndo}
              onRedo={handleRedo}
            />
            <ButtonGroup>
              <Button
                onClick={onSave}
                disabled={loading || !hasChanges}
                variant="outline"
              >
                Save
                {loading && <LoadingSpinner className="ml-3 mr-0" />}
              </Button>
              <ButtonGroupSeparator />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => setPlanDeleteAlert(true)}
                    variant={'destructive'}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Workout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          </div>
        </div>
      </div>
      <div className={cn(editorPageRailClassName, 'mt-4')}>
        <WorkoutEditorWorkspace
          workout={workout}
          updateWorkout={updateWorkout}
          setHasChanges={() => {}}
          className={editorPageWorkspaceClassName}
        >
          <div className="min-w-0 flex-1">
            <EditWorkoutView
              hasChanges={hasChanges}
              setHasChanges={() => {}}
              workout={workout as Workout}
              updateWorkout={updateWorkout}
              name={{
                value: name,
                valueChange: (value) => {
                  history.commitDoc(
                    (prev) => ({ ...prev, name: value }),
                    { coalesceKey: 'plan-name' },
                  )
                },
              }}
              description={{
                value: description,
                valueChange: (value) => {
                  history.commitDoc(
                    (prev) => ({ ...prev, description: value }),
                    { coalesceKey: 'plan-description' },
                  )
                },
              }}
              removeExercise={removeExercise}
              template={true}
              hideName
            />
            <div ref={bottomRef} />
            <AiWorkoutBar
              exerciseCollection={workout.exerciseCollection}
              commit={(producer) => {
                history.commitDoc((prev) => ({
                  ...prev,
                  workout: producer(prev.workout),
                }))
              }}
              onEdited={() => {
                setTimeout(() => {
                  bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
                }, 200)
              }}
            />
          </div>
          <ExerciseSelectList
            exerciseSelected={handleExerciseSelectList}
            enableDragToWorkout
            onExerciseCreated={(exercise) => {
              handleExerciseSelectList(exercise)
            }}
            className={`hidden xl:flex ${WORKOUT_SIDEBAR_STICKY_CLASS}`}
          />
        </WorkoutEditorWorkspace>
      </div>
    </>
  )
}

export default WorkoutView
